import { Component, OnInit } from '@angular/core';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { Router } from '@angular/router';
import { first } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoggerService } from '@app/shared/services/logger.service';
import { environment } from '../../../../../environments/environment';
import { GlobalConfiguration, GlobalConfigurationService } from '@app/shared/services/global-configuration.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { ProvinceList } from '@app/shared/constants';
import { JobTriggerService } from '../../../../shared/services/job-trigger.service';
import { HelpersService } from '../../../../shared/services/helpers.service';

@Component({
  standalone: false,
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
})
export class ConfigurationPageComponent implements OnInit {
  public loading: boolean = true;
  public user: AdminUser;
  public canEdit: boolean = false;
  public CDN_URL: string = environment.CDN_URL;
  public globalConfigurationForm: FormGroup;
  public globalConfiguration: GlobalConfiguration;
  public provinces = ProvinceList;

  public selectedProvinceToAdd: string | null = null;
  public availableProvinceOptions: { id: string; text: string }[] = [];
  public isProd = environment.production;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private logger: LoggerService,
    private router: Router,
    private globalConfigurationService: GlobalConfigurationService,
    private bannerNotificationsService: BannerNotificationsService,
    private jobTriggerService: JobTriggerService,
    private helpersService: HelpersService,
  ) {}
  ngOnInit() {
    this.userService
      .getAuthenticatedUser([AdminScopes.VIEW_CONFIGURATION])
      .pipe(first())
      .subscribe(
        (adminUser: AdminUser) => {
          if (adminUser) {
            this.user = adminUser;
            this.canEdit = this.userService.hasAuthScope(this.user, [AdminScopes.MODIFY_CONFIGURATION]);
            this.getGlobalConfiguration();
          } else {
            this.router.navigate(['login']);
          }
        },
        (_) => {
          this.router.navigate(['home']);
        }
      );
  }

  public updateGlobalConfiguration(): void {
    if (this.canEdit) {
      this.loading = true;
      const globalConfigurationValue = this.globalConfigurationForm.value;

      const params: GlobalConfiguration = {
        byoa_charge_amount: globalConfigurationValue.byoaChargeAmount,
        return_centre_renewal: globalConfigurationValue.returnCentreRenewal,
        maximum_wallet_fund_attempts: globalConfigurationValue.maximumWalletFundAttempts,
        wallet_fund_time_window: globalConfigurationValue.walletFundTimeWindow,
        signe_local_margin: globalConfigurationValue.signeLocalMargin,
        signe_local_courier_modifier: globalConfigurationValue.signeLocalCourierModifier,
        allow_first_time_discount:  globalConfigurationValue.allowFirstTimeDiscount,
        first_time_discount: globalConfigurationValue.firstTimeDiscount,
        allow_referral:  globalConfigurationValue.allowReferral,
        business_referral_amount: globalConfigurationValue.businessReferralAmount,
        reward_percent: JSON.stringify(this.buildRewardPercentOverrides()),
        min_payout_amount: globalConfigurationValue.minPayoutAmount,
        allow_reward: globalConfigurationValue.allowReward,
        check_blacklist: globalConfigurationValue.checkBlacklist,
        external_api_wallet_minimum: globalConfigurationValue.externalApiWalletMinimum,
        external_api_wallet_margin_percent: globalConfigurationValue.externalApiWalletSafetyMargin,
        stop_acc_ecommerce_calls: globalConfigurationValue.stopAccEcommerceCalls,
        recon_needs_attention_company_threshold: this.helpersService.valueOrUndefined(globalConfigurationValue.reconNeedsAttentionCompanyThreshold),
        recon_needs_attention_shipment_threshold: this.helpersService.valueOrUndefined(globalConfigurationValue.reconNeedsAttentionShipmentThreshold),
        recon_pending_light_min_total: this.helpersService.valueOrUndefined(globalConfigurationValue.reconPendingLightMinTotal),
      };

      this.globalConfigurationService.updateGlobalConfiguration(params).subscribe({
        next: (res: GlobalConfiguration): void => {
          this.logger.log('Updated configuration successfully', res);
          this.bannerNotificationsService.success(`Configuration was updated successfully`);
        },
        error: (error: Error): void => {
          this.bannerNotificationsService.error(`Updating configuration failed`);
          this.logger.error('Failed to update configuration', error);
        },
        complete: (): void => {
          this.getGlobalConfiguration()
        }
      })
    }
  }

  private createForm(): void {
    this.globalConfigurationForm = this.formBuilder.group({
      byoaChargeAmount: [
        {
          value: this.globalConfiguration.byoa_charge_amount || 0,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(99), Validators.min(0)],
      ],
      returnCentreRenewal: [
        {
          value: this.globalConfiguration.return_centre_renewal || 0,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(1000), Validators.min(0)],
      ],
      maximumWalletFundAttempts: [
        {
          value: this.globalConfiguration.maximum_wallet_fund_attempts || 0,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(99), Validators.min(0)],
      ],
      walletFundTimeWindow: [
        {
          value: this.globalConfiguration.wallet_fund_time_window || 0,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(10000), Validators.min(60)],
      ],
      signeLocalMargin: [
        {
          value: this.globalConfiguration.signe_local_margin || 5,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(1000), Validators.min(0)],
      ],
      signeLocalCourierModifier: [
        {
          value: this.globalConfiguration.signe_local_courier_modifier || 15,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(1000), Validators.min(0)],
      ],
      allowFirstTimeDiscount: [
        {
          value: +(this.globalConfiguration.allow_first_time_discount || '0') === 1 ? true : false,
          disabled: !this.canEdit,
        },
        [],
      ],
      firstTimeDiscount: [
        {
          value: this.globalConfiguration.first_time_discount || 20,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(100), Validators.min(0)],
      ],
      stopAccEcommerceCalls: [
        {
          value: +(this.globalConfiguration.stop_acc_ecommerce_calls || '0') === 1 ? true : false,
          disabled: !this.canEdit,
        },
        [],
      ],
      allowReferral: [
        {
          value: +(this.globalConfiguration.allow_referral || '0') === 1 ? true : false,
          disabled: !this.canEdit,
        },
        [],
      ],
      businessReferralAmount: [
        {
          value: this.globalConfiguration.business_referral_amount || 25,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(100), Validators.min(0)],
      ],
      allowReward: [
        {
          value: +(this.globalConfiguration.allow_reward || '0') === 1 ? true : false,
          disabled: !this.canEdit,
        },
        [],
      ],
      minPayoutAmount: [
        {
          value: this.globalConfiguration.min_payout_amount || 5,
          disabled: !this.canEdit,
        },
        [Validators.required, Validators.max(100), Validators.min(0)],
      ],
      rewardPercentOverrides: this.formBuilder.array([]),
      checkBlacklist: [
        {
          value: +(this.globalConfiguration.check_blacklist || '0') === 1,
          disabled: !this.canEdit,
        },
        [],
      ],
      externalApiWalletMinimum: [ {
        value: this.globalConfiguration.external_api_wallet_minimum || 0,
        disabled: !this.canEdit,
      },],
      externalApiWalletSafetyMargin: [ {
        value: this.globalConfiguration.external_api_wallet_margin_percent || 0,
        disabled: !this.canEdit,
      },],
      reconNeedsAttentionCompanyThreshold: [
        {
          value: this.globalConfiguration.recon_needs_attention_company_threshold || null,
          disabled: !this.canEdit,
        },
        [Validators.min(0)],
      ],
      reconNeedsAttentionShipmentThreshold: [
        {
          value: this.globalConfiguration.recon_needs_attention_shipment_threshold || null,
          disabled: !this.canEdit,
        },
        [Validators.min(0)],
      ],
      reconPendingLightMinTotal: [
        {
          value: this.globalConfiguration.recon_pending_light_min_total || null,
          disabled: !this.canEdit,
        },
        [Validators.min(0)],
      ],
    });

    this.globalConfigurationForm.updateValueAndValidity();
    this.populateProvinceOverrides();
    this.setupProvinceControlDisabling();
    this.updateAvailableProvinceOptions();
    this.loading = false;
  }

  private buildRewardPercentOverrides(): Record<string, number> {
    const overridesArray = this.globalConfigurationForm.get('rewardPercentOverrides') as FormArray;
    const rewardPercent: Record<string, number> = {};

    overridesArray.controls.forEach(group => {
      const province = group.get('province')?.value;
      const percent = +group.get('percent')?.value;

      if (province) {
        rewardPercent[province] = percent;
      }
    });

    return rewardPercent;
  }

  private setupProvinceControlDisabling(): void {
    this.rewardOverrides.controls.forEach(group => {
      const provinceControl = group.get('province');
      if (!provinceControl) return;

      // Initially disable/enable based on value
      if (provinceControl.value === 'default') {
        provinceControl.disable({ emitEvent: false });
      } else {
        provinceControl.enable({ emitEvent: false });
      }

      provinceControl.valueChanges.subscribe(value => {
        if (value === 'default') {
          provinceControl.disable({ emitEvent: false });
        } else {
          provinceControl.enable({ emitEvent: false });
        }
      });
    });
  }

  private populateProvinceOverrides(): void {
    const config = this.globalConfiguration.reward_percent || { default: 1 };
    const overrides = this.rewardOverrides;

    Object.entries(config).forEach(([provinceCode, percent]) => {
      overrides.push(this.formBuilder.group({
        province: [{ value: provinceCode, disabled: provinceCode === 'default' }],
        percent: [percent, [Validators.required, Validators.min(0), Validators.max(100)]],
      }));
    });
  }

  get rewardOverrides(): FormArray {
    return this.globalConfigurationForm.get('rewardPercentOverrides') as FormArray;
  }

  private updateAvailableProvinceOptions(): void {
    const selected = this.rewardOverrides.controls.map(ctrl => ctrl.get('province')?.value);
    this.availableProvinceOptions = this.provinces.filter(p => !selected.includes(p.id));
  }

  get defaultRewardPercent(): number {
    const configRaw = this.globalConfiguration?.reward_percent;
    try {
      const config = typeof configRaw === 'string' ? JSON.parse(configRaw) : configRaw;
      return config?.['default'] ?? 1;
    } catch (err) {
      this.logger.error('Failed to parse reward_percent:', err);
      return 1;
    }
  }

  public addProvinceOverride(): void {
    if (!this.selectedProvinceToAdd) return;

    this.rewardOverrides.push(this.formBuilder.group({
      province: [this.selectedProvinceToAdd, Validators.required],
      percent: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    }));

    this.selectedProvinceToAdd = null;
    this.updateAvailableProvinceOptions();
  }

  public removeProvinceOverride(index: number): void {
    this.rewardOverrides.removeAt(index);
    this.updateAvailableProvinceOptions();
  }

  public triggerRewardJob() {
    this.loading = true;
    this.jobTriggerService.triggerRewardJob().subscribe({
      next: () => {
        this.logger.log('Reward job triggered successfully');
        this.loading = false;
      },
      error: (err) => {
        this.logger.error('Error triggering reward job:', err);
        this.loading = false;
      }
    })
  }

  public triggerReferralJob() {
    this.loading = true;
    this.jobTriggerService.triggerReferralJob().subscribe({
      next: () => {
        this.logger.log('Referral job triggered successfully');
        this.loading = false;
      },
      error: (err) => {
        this.logger.error('Error triggering referral job:', err);
        this.loading = false;
      }
    })
  }

  public triggerMultiLegTrackingSyncJob() {
    this.loading = true;
    this.jobTriggerService.triggerMultiLegTrackingSyncJob().subscribe({
      next: () => {
        this.logger.log('MultiLeg tracking sync job triggered successfully');
        this.loading = false;
      },
      error: (err) => {
        this.logger.error('Error triggering MultiLeg tracking sync job:', err);
        this.loading = false;
      }
    })
  }

  private getGlobalConfiguration(): void {
    this.loading = true;
    this.globalConfigurationService.getGlobalConfiguration().subscribe({
      next: (res: GlobalConfiguration): void => {

      if (typeof res.reward_percent === 'string') {
        try {
          const config = JSON.parse(res.reward_percent);
          res.reward_percent = config;
        } catch (err) {
          this.logger.error('Failed to parse reward_percent:', err);
          res.reward_percent = { default: 1 };
        }
      }
        this.globalConfiguration = res;
        this.createForm();
      },
      error: (error: Error): void => {
        this.logger.error('Failed to get global configuration', error);
      }
    })
  }
}
